/**
 * Application Configuration
 *
 * Environment-specific configuration and constants.
 *
 * Runtime values injected via `window.__ENV__` (populated by docker-entrypoint.sh)
 * take precedence over build-time `import.meta.env` values so the same Docker
 * image can be deployed to any environment.
 */

import { API_ROUTES } from './api-routes';

/** Runtime env injected by docker-entrypoint.sh → env-config.js */
const runtimeEnv: Record<string, string | undefined> =
  typeof window !== 'undefined'
    ? ((window as unknown as Record<string, unknown>).__ENV__ as Record<string, string | undefined> ?? {})
    : {};

/** Return the first non-empty string, or fallback. */
export function env(key: string, fallback = ''): string {
  return runtimeEnv[key] || import.meta.env[key] || fallback;
}

// ── BEGIN LOCAL_ONBOARDING_OVERRIDE ──────────────────────────────────────────
/**
 * TEMPORARY (local dev only). Resolve `VITE_ONBOARDING_ENDPOINT`, mirroring the
 * API's AND gate in apps/tms-api-settings/src/v1/local/localOnboardingStore.ts:
 * the flag must say `local` AND this must be a dev build.
 *
 * Both `MODE` and `DEV` are checked, and `MODE` is the load-bearing one. `DEV`
 * alone is NOT a safe fence here: the custom `ENVIRONMENT` variable does not control
 * Vite's build mode, so a `nx build` bundle is emitted with
 * `MODE:"production"` but `DEV:true`. `MODE` comes from the Vite command itself
 * (`build` → production, `serve` → development), so it can't be knocked off course
 * by an env file.
 *
 * Resolved here rather than in settings.store.ts so `import.meta` stays confined
 * to this file — the stores are loaded by Jest, which cannot parse it.
 *
 * To remove: see the checklist in the API file named above.
 */
function resolveOnboardingEndpoint(): string {
  const value = env('VITE_ONBOARDING_ENDPOINT').trim().toLowerCase();
  if (value !== 'local') return '';
  if (import.meta.env.MODE !== 'production' && import.meta.env.DEV) return 'local';
  // Asked for but refused. Fail closed, but say so — a silently-ignored value is
  // indistinguishable from a broken feature.
  console.warn(
    '[LOCAL_ONBOARDING_OVERRIDE] VITE_ONBOARDING_ENDPOINT=local ignored: this is not a dev build. ' +
      'Onboarding state will come from the JWT claim / localStorage as usual.',
  );
  return '';
}
// ── END LOCAL_ONBOARDING_OVERRIDE ────────────────────────────────────────────

export const config = {
  app: {
    name: 'TMS Admin Dashboard' as const,
    version: env('VITE_APP_VERSION', '1.0.0'),
    environment: import.meta.env.MODE,
  },
  api: {
    baseUrl: env('VITE_API_URL', 'http://localhost:5000'),
    timeout: 30000,
  },
  /** Hospital context — populated at runtime by setAuthContext() after login. */
  hospitalId: '',
  /**
   * LOCAL_ONBOARDING_OVERRIDE — TEMPORARY (local dev only).
   * `'local'` makes checkSettingsOnboarding() ask the API for
   * is_settings_configured instead of trusting the login JWT claim, so the local
   * Postgres override is actually visible in the browser. Must match
   * ONBOARDING_ENDPOINT on the API. Empty string in every other case — see
   * resolveOnboardingEndpoint() above.
   */
  onboardingEndpoint: resolveOnboardingEndpoint(),
  /** Enterprise context — set via VITE_ENTERPRISE_ID env var (rarely changes per user). */
  enterpriseId: env('VITE_ENTERPRISE_ID'),
  /** Tenant context — populated at runtime by setAuthContext() after login. */
  tenantId: '',
  /** Staff ID — populated at runtime by setAuthContext() after login. */
  staffId: '',
  /** UUID of the user creating a record (maps to `created_by` in API payloads). Populated after login. */
  createdBy: '',
  /** UUID recorded as `received_by` in milk-receivable POST payloads. Populated after login. */
  receivedBy: '',
  logging: {
    level: (import.meta.env.DEV ? 'debug' : 'info') as 'debug' | 'info',
    console: true,
    beaconUrl: import.meta.env.PROD ? `${API_ROUTES.SERVICE}/logs` : undefined,
    beaconLevel: 'warn' as const,
    serviceName: 'admin-dashboard',
  },
  telemetry: {
    // Enable whenever the NR Browser app ID is present at runtime — not tied
    // to build-time PROD so that dev/staging containers can opt in via env vars.
    enabled: !!env('NEW_RELIC_BROWSER_APP_ID'),
    serviceName: 'admin-dashboard',
    environment: import.meta.env.MODE,
    beaconUrl: `${API_ROUTES.SERVICE}/telemetry`,
    applicationId: env('NEW_RELIC_BROWSER_APP_ID'),
    licenseKey: env('NEW_RELIC_BROWSER_LICENSE_KEY'),
  },
  features: {
    enableDevTools: import.meta.env.DEV,
    enableMockData: env('VITE_ENABLE_MOCK') === 'true',
  },
};

export type AppConfig = typeof config;

// LOCAL_ONBOARDING_OVERRIDE
/**
 * Whether the local Postgres onboarding override is active for this session.
 * The single place every consumer should check this — resolveOnboardingEndpoint()
 * above resolves *what* config.onboardingEndpoint is; this is the one function
 * that decides what it means, so there's exactly one name to grep for.
 */
export function isLocalOnboardingOverrideActive(): boolean {
  return config.onboardingEndpoint === 'local';
}

/**
 * Update auth-derived config values after login or on page-reload hydration.
 *
 * Call this after receiving tokens from the auth callback so that
 * `config.staffId`, `config.hospitalId`, `config.tenantId`, `config.createdBy`
 * and `config.receivedBy` reflect the authenticated user's context instead of
 * the build-time env-var defaults.
 */
export function setAuthContext(ctx: {
  staffId?: string;
  hospitalId?: string;
  tenantId?: string;
}): void {
  if (ctx.hospitalId) {
    (config as Record<string, unknown>).hospitalId = ctx.hospitalId;
  }
  if (ctx.tenantId) {
    (config as Record<string, unknown>).tenantId = ctx.tenantId;
  }
  if (ctx.staffId) {
    (config as Record<string, unknown>).staffId = ctx.staffId;
    (config as Record<string, unknown>).createdBy = ctx.staffId;
    (config as Record<string, unknown>).receivedBy = ctx.staffId;
  }
}

/**
 * Reset auth-derived config values on logout.
 *
 * Clears `config.staffId`, `config.hospitalId`, `config.tenantId`,
 * `config.createdBy` and `config.receivedBy` so stale values from the
 * previous session do not leak into subsequent requests.
 */
export function clearAuthContext(): void {
  (config as Record<string, unknown>).staffId = '';
  (config as Record<string, unknown>).hospitalId = '';
  (config as Record<string, unknown>).tenantId = '';
  (config as Record<string, unknown>).createdBy = '';
  (config as Record<string, unknown>).receivedBy = '';
}
